import { useState, useCallback, useEffect, useMemo, useRef, Suspense, useContext } from 'react';
import type { ComponentType } from 'react';
import { useQueryState, parseAsString } from 'nuqs';
import { useNavigate } from 'react-router-dom';
import { Eye, Info, Loader2, MessageCircle } from 'lucide-react';
// plugin-chat is an intentional core dependency: the agent stream view's primary
// interaction mode is the chat UI. A future stream-renderer extension point could
// make this pluggable, but for now it's tightly coupled by design.
import { ChatInput } from '@reflex/plugin-chat/web/components/ChatInput';
import { LaunchMentionMenu } from './launch-agent-dialog/LaunchMentionMenu';
import { AgentContextStack } from './AgentContextStack';
import { MCP_SERVERS_ATTACHMENT_ID, useMcpServerAttach } from './useMcpServerAttach';
import {
  SDKChatView,
  type PagedHistoryControls,
} from '@reflex/plugin-chat/web/components/sdk-chat/SDKChatView';
import {
  useAgentStreamFull,
  useAgentStreamPage,
  useLoadEarlierEvents,
} from '@/hooks/agents/useAgentStream';
import { mergeFreshWindow } from '@/hooks/agents/stream-paging';
import { useAgentSessionIndex } from '@/hooks/agents/useAgentSessionIndex';
import { useJumpToTurn } from '@/hooks/agents/useJumpToTurn';
import { useTurnPager } from '@/hooks/agents/useTurnPager';
import { latestContextUsage } from '@reflex/plugin-chat/web/components/sdk-chat/context-usage';
import {
  CHAT_COLUMN_CLASS,
  chatColumnStyle,
  useChatColumnWidth,
} from '@reflex/plugin-chat/web/components/sdk-chat/chat-column';
import { ChatColumnResizer } from '@reflex/plugin-chat/web/components/sdk-chat/ChatColumnResizer';
import type { AvailableCommand } from '@reflex/plugin-chat/web/components/sdk-chat/types';
import { AgentSettingsChip } from '@reflex/ui/components/agent/AgentSettingsChip';
import type { AgentThreadListEntry } from '@reflex/ui/components/agent/AgentThreadList';
import { MarkdownAgentIdProvider } from '@reflex/ui/components/content/MarkdownContent';
import {
  ImageCommentProvider,
  type ImageComment,
} from '@reflex/ui/components/content/ImageCommentContext';
import { formatImageCommentMessage } from './image-comment-message';
import { ContextUsageMeter } from '@reflex/ui/components/agent/ContextUsageMeter';
import { toModelProvider } from '@/components/agent/to-model-provider';
import { useSubscriptionSwitch } from './agent-detail/useSubscriptionSwitch';
import { useModelSwitch } from './agent-detail/useModelSwitch';
import { useAgentTypeRegistry } from '@/lib/agent-type-registry';
import { AgentActivityDock } from './AgentActivityDock';
import { backgroundWorkLabel } from '@/hooks/agents/agent-activity-utils';
import { AgentDetailPanel } from './AgentDetailPanel';
import { AgentPreviewPanel } from './AgentPreviewPanel';
import { AgentStreamDataContext, useAgentStreamData } from './AgentStreamDataContext';
import { DefaultAgentStreamDataProvider } from './DefaultAgentStreamDataProvider';
import { useAgentDraft } from './agent-thread-draft/useAgentDraft';
import { useAgentPluginContributions } from '@/hooks/config/useAgentPluginContributions';
import { useFeatureFlagStatus } from '@/hooks/config/useFeatureFlag';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useAgentAccess } from '@/hooks/agents/useAgentAccess';
import { useDevboxStatus } from '@/hooks/agents/useDevboxStatus';
import { isDevboxStatusPollEligible } from '@reflex/ui/client/hooks';
import { useDocumentTitle } from '@reflex/ui/hooks/useDocumentTitle';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { useWakeLock } from '@/hooks/browser/useWakeLock';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { AxonViewContext } from './AxonViewContext';
import { AgentHeaderBar, MobileViewStrip, MobileToolsSheet } from './agent-stream-bars';
import { AgentRailToolbar } from './agent-stream-bars/AgentRailToolbar';
import { ShareRequestsBanner } from './ShareRequestsBanner';
import { useAgentTabLayout, pinnedAgentViews } from './agent-stream-bars/useAgentTabLayout';
import { useAgentActions, AgentActionDialogs } from '@/components/agent/agent-actions';
import { useRegisterPageCommands, type PageCommand } from '@/components/layout/page-commands';
import { useHotkey } from '@/hooks/ui/useHotkey';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { VerticalTabRail } from './agent-stream-bars/VerticalTabRail';
import type { Agent } from '@/lib/types';
import { formatModelLabel, getGitRepoAttachment } from '@reflex/shared';
import { filterAgentTabsBySandboxCapabilities, type ResolvedAgentTab } from '@/lib/plugin-registry';

const CORE_VIEW_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  'sdk-chat': MessageCircle,
  preview: Eye,
};

const noop = () => {};

const CORE_VIEW_LABEL: Record<string, string> = {
  'sdk-chat': 'Chat',
  preview: 'Preview',
};

type AgentView = string;

function isDevboxShutDown(
  agent: Pick<Agent, 'status' | 'devboxId'>,
  liveDevboxIsGone: boolean,
): boolean {
  if (agent.status === 'terminated') return true;
  if (agent.status === 'completed' && !agent.devboxId) return true;
  if (agent.status === 'stopped' || agent.status === 'stopping') return false;
  return liveDevboxIsGone;
}

interface AgentStreamViewProps {
  agent: Agent;
  onArchived?: () => void;
  onToggleSidebar?: () => void;
  /** Show the sidebar toggle at every viewport (sessions dock); default mobile only. */
  sidebarToggleAlwaysVisible?: boolean;
  /**
   * Optional event id to scroll-to and flash-highlight when the view opens.
   * Typically arrives via the `?eventId=` URL param from the Axon page.
   */
  highlightEventId?: string;
  /**
   * Called once the highlight has been applied so the caller can clear the
   * URL param (prevents re-triggering the flash on every rerender).
   */
  onHighlightConsumed?: () => void;
}

/**
 * Inner component that reads from AgentStreamDataContext.
 * Wrapped by the public AgentStreamView which ensures a provider exists.
 */
function AgentStreamViewInner({
  agent,
  onArchived,
  onToggleSidebar,
  sidebarToggleAlwaysVisible,
  highlightEventId: externalHighlightEventId,
  onHighlightConsumed,
  numberHotkeys = false,
}: AgentStreamViewProps & {
  /**
   * Bind the document-global ⌘/Ctrl+Alt+digit view-switching shortcut. Set by the
   * public wrapper, not callers: only the live-data surface gets it — a page
   * can embed several provided-context replays (marketing blocks, docs), and
   * each would otherwise register its own competing global listener.
   */
  numberHotkeys?: boolean;
}) {
  const {
    events,
    activity,
    isWorking,
    isTurnWorking = isWorking,
    chatText,
    replay,
    send,
    redirect,
    interrupt,
    hideActions,
    queue,
    updateMessage,
    removeMessage,
    reorderQueue,
    sendQueuedNow,
    pendingMessages,
    retryPending,
    dismissPending,
  } = useAgentStreamData();

  const { agentHeaderBadges } = usePlugins();
  const { agentActions, agentTabs: allAgentTabs } = useAgentPluginContributions();
  // Capability-gated tabs (e.g. the computer-use Desktop tab) only show for
  // agents launched with that devbox capability enabled.
  const agentTabs = useMemo(
    () => filterAgentTabsBySandboxCapabilities(allAgentTabs, agent.sandboxOptions),
    [allAgentTabs, agent.sandboxOptions],
  );
  const agentTypeRegistry = useAgentTypeRegistry();
  const navigate = useNavigate();
  const runningAgentTypeInfo = agentTypeRegistry.get(agent.agentType);
  const isDesktop = useIsDesktop();
  // Stealth replays blank `chatText` between typing animations, so `chatText`
  // alone would flicker. It still covers story fixtures that omit `replay`.
  const isReplay = replay === true || chatText !== undefined;
  // Lets the chat-input key icon switch the running agent's subscription — the
  // same wiring the detail panel's AgentSubscriptionSelector uses.
  const subscriptionSwitch = useSubscriptionSwitch(agent);
  // What this caller may do with the run, as the server answered on the
  // agent payload. A `view` grant reads it and nothing more.
  const agentAccess = useAgentAccess(agent);
  // The model the stream reports as answering. Only the context meter uses it;
  // the model chip shows `agent.model`, which every switch updates.
  const [activeModel, setActiveModel] = useState<string | null>(null);
  const modelSwitch = useModelSwitch(agent, {
    canWrite: agentAccess.canWrite,
    isReplay,
  });
  // The live probe catches a box shut down outside Reflex. Retention cleanup
  // clears devboxId but deliberately leaves a completed run completed, so that
  // shape is also an unresumable session. Replays never poll.
  const devboxStatusPollEligible =
    !isReplay && isDevboxStatusPollEligible(agent.status, agent.devboxId);
  const { data: devboxStatusData } = useDevboxStatus(agent.id, {
    enabled: devboxStatusPollEligible,
  });
  const devboxStatusIsGone =
    devboxStatusData?.status === 'shutdown' || devboxStatusData?.status === 'failure';
  const liveDevboxIsGone = devboxStatusPollEligible && devboxStatusIsGone;
  const devboxShutDown = isDevboxShutDown(agent, liveDevboxIsGone);
  const [detailOpen, setDetailOpen] = useState(false);
  const [chatColumnWidth] = useChatColumnWidth();
  useWakeLock(agent.status === 'running');
  useDocumentTitle(agent.name);
  const [view, setViewParam] = useQueryState('tab', parseAsString.withDefault('sdk-chat'));
  const setView = useCallback(
    (next: AgentView) => {
      void setViewParam(next === 'sdk-chat' ? null : next, { history: 'replace' });
    },
    [setViewParam],
  );
  const [highlightEventId, setHighlightEventId] = useState<string | undefined>(
    externalHighlightEventId,
  );
  const [lastExternalHighlight, setLastExternalHighlight] = useState<string | undefined>(
    externalHighlightEventId,
  );
  const [availableCommands, setAvailableCommands] = useState<AvailableCommand[]>([]);
  // True while an AskUserQuestion prompt is awaiting a reply — the prompt is
  // lifted into an overlay and the composer is disabled so the user answers it
  // instead of typing past it.
  const [questionPending, setQuestionPending] = useState(false);
  // Agents this session launched through Reflex MCP, as the chat view parses
  // them from the transcript's tool calls. Shown in the activity dock beside
  // the stream-derived sub-agents; each entry's id is the child agent's id.
  const [launchedSubagents, setLaunchedSubagents] = useState<AgentThreadListEntry[]>([]);
  const orgRoutes = useOrgRoutes();
  const openDockSubagent = useCallback(
    (id: string) => {
      // A launched-agent row is a real agent: open it. Stream rows carry an
      // `agent.subagent` event id and keep the scroll-and-flash behavior.
      if (launchedSubagents.some((entry) => entry.id === id)) {
        // The id was parsed out of an MCP tool result in the transcript, not
        // read from our own records, so only interpolate it into a route when
        // it is shaped like an entity id — a crafted payload ("x/../y", "?",
        // "#") must not steer navigation.
        if (/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(id)) void navigate(orgRoutes.agent(id));
        return;
      }
      setHighlightEventId(id);
    },
    [launchedSubagents, navigate, orgRoutes],
  );

  // Live MCP attach for the running session: the composer's "+" (and the `@`
  // menu's MCP category) push a server onto the devbox mid-run.
  const mcpAttach = useMcpServerAttach(agent);
  const canAttachMcp = !isReplay && agentAccess.canWrite && mcpAttach.canAttach;
  // Rollout gate for the composer's context control. Off for everyone until a
  // user is added to the flag's allowlist, and a display gate only: the `@`
  // menu still reaches the same categories, and context already on the run
  // keeps working while the stack is hidden.
  //
  // The loading-aware read, not the plain `useFeatureFlag`: that one reports
  // false for the whole of every refetch, and the flags query is invalidated on
  // window focus, org switch, and impersonation. This composer stays mounted
  // across all three, so the plain hook would blink the control out of a live
  // session (destroying an open context popover) and back.
  const { enabled: contextStackEnabled } = useFeatureFlagStatus('agent_context_stack_enabled');
  // Lets the "+" trigger open the same `@`-mention menu the composer does.
  const openMentionRef = useRef<(() => void) | null>(null);
  // Attachment configs already on the run, keyed by attachmentId, so mention
  // providers can subtract items that are already attached.
  const appliedAttachments = useMemo(
    () => Object.fromEntries((agent.attachments ?? []).map((a) => [a.attachmentId, a])),
    [agent.attachments],
  );
  // Replay providers drive the input externally via `chatText`; in that mode
  // we don't want to read or write a per-agent draft.
  const {
    draftText,
    draftAttachments,
    chatInputKey,
    setDraftText,
    setDraftAttachments,
    clearDraft,
  } = useAgentDraft(agent.id, !isReplay);

  if (externalHighlightEventId !== lastExternalHighlight) {
    setLastExternalHighlight(externalHighlightEventId);
    if (externalHighlightEventId && externalHighlightEventId !== highlightEventId) {
      setHighlightEventId(externalHighlightEventId);
    }
  }

  // Paged session history (indexed agents load last-turn-first): the loaded
  // window's page info, the session index behind the rail's unloaded turns,
  // and the load-earlier machinery. All of it is absent (`pagedHistory`
  // undefined) for legacy agents, whose transcript loads whole as before.
  const streamPage = useAgentStreamPage(agent.id);
  // The paged window is the *transcript's* load model; several consumers on
  // this page semantically need the whole history: plugin tabs (changed-file
  // sets, "Copy all", assessments) and boot-time signals — `agent.dev_server`
  // is emitted at boot, falls out of the window, and gates whether the
  // Preview tab exists at all, which is why this fetch is eager rather than
  // per-tab. The one-shot full fetch is folded under the live window (which
  // the socket keeps appending to) so nothing that arrives later is missed.
  // Legacy agents (`streamPage` null) already hold their full history in
  // `events`; replay surfaces provide their own complete event set.
  const fullStream = useAgentStreamFull(!isReplay && streamPage ? agent.id : null);
  const fullEvents = useMemo(
    () =>
      !isReplay && streamPage ? mergeFreshWindow(fullStream.data ?? [], events ?? []) : events,
    [isReplay, streamPage, fullStream.data, events],
  );
  const sessionIndex = useAgentSessionIndex(streamPage ? agent.id : null);
  const loadEarlier = useLoadEarlierEvents(agent.id);
  const { mutate: loadEarlierPage, isPending: isLoadingEarlier } = loadEarlier;
  // Continuous scroll: up to ten previous turns staying
  // prefetched above the window, committed a turn at a time when the
  // transcript's top sentinel makes contact. The pager owns every
  // page-back entry point: its returned `requestPageBack` parks the fill
  // before firing the mutation, so the pill, the sentinel fallback, and
  // the rail jump never race an in-flight fill fetch.
  const { commitStagedTurn, requestPageBack } = useTurnPager({
    agentId: !isReplay && streamPage ? agent.id : null,
    page: streamPage,
    index: sessionIndex.data,
    loadEarlier: loadEarlierPage,
  });
  // A rail click on an unloaded turn: keep paging until its sequence is in
  // the loaded window, then scroll to its prompt via the existing
  // highlight-and-scroll channel. A failed page fetch abandons the jump.
  const { jumpSeq, jumpToTurn } = useJumpToTurn({
    streamPage,
    events,
    loadEarlier: { mutate: requestPageBack, isPending: isLoadingEarlier },
    onTargetLoaded: setHighlightEventId,
  });

  // Every prompt row in the session index, session order — steers included
  // (`opensTurn: false`), since the rail's merge is what decides which rows
  // are turns and which loaded prompts they correspond to.
  const indexTurns = useMemo(() => {
    if (!streamPage || !sessionIndex.data) return [];
    return sessionIndex.data.entries.flatMap((row) =>
      row.entry.type === 'prompt'
        ? [
            {
              seqNumber: row.seqNumber,
              text: row.entry.data.text,
              opensTurn: row.entry.data.opensTurn,
            },
          ]
        : [],
    );
  }, [streamPage, sessionIndex.data]);

  const pagedHistory = useMemo<PagedHistoryControls | undefined>(
    () =>
      streamPage
        ? {
            hasMore: streamPage.hasMore,
            isLoadingEarlier,
            onLoadEarlier: requestPageBack,
            indexTurns,
            onJumpToTurn: jumpToTurn,
            jumpPendingSeq: jumpSeq,
            commitStagedTurn,
          }
        : undefined,
    [
      streamPage,
      isLoadingEarlier,
      requestPageBack,
      indexTurns,
      jumpToTurn,
      jumpSeq,
      commitStagedTurn,
    ],
  );

  // Context-usage meter for the composer. Only agents that declare
  // `reportsContextUsage` (and provide a context window for the running model)
  // get one; the figure is derived from the token usage in the event stream.
  // The window is sized against the model actually answering, which lags a
  // switch until the current turn ends.
  const contextUsage = useMemo(() => {
    if (!runningAgentTypeInfo?.reportsContextUsage) return null;
    const maxTokens = agentTypeRegistry.contextWindowTokens(
      agent.agentType,
      activeModel,
      agent.model,
    );
    return latestContextUsage(events, maxTokens);
  }, [events, agentTypeRegistry, runningAgentTypeInfo, agent.agentType, agent.model, activeModel]);

  const devServerPort = useMemo(() => {
    if (!fullEvents) return null;
    for (let i = fullEvents.length - 1; i >= 0; i--) {
      if (fullEvents[i].type === 'agent.dev_server') {
        const p = parseEventPayload<{ port?: number }>(fullEvents[i].payload);
        if (p?.port) return p.port;
      }
    }
    return null;
  }, [fullEvents]);

  const tabMap = useMemo(() => {
    const map = new Map<string, ResolvedAgentTab>();
    for (const tab of agentTabs) map.set(tab.id, tab);
    return map;
  }, [agentTabs]);

  const viewIconMap = useMemo<Record<string, React.ComponentType<{ className?: string }>>>(() => {
    const icons: Record<string, React.ComponentType<{ className?: string }>> = {
      ...CORE_VIEW_ICON,
    };
    for (const tab of agentTabs) icons[tab.id] = tab.icon;
    return icons;
  }, [agentTabs]);

  const availableViews = useMemo<AgentView[]>(() => {
    const v: AgentView[] = ['sdk-chat'];
    for (const tab of agentTabs) v.push(tab.id);
    if (devServerPort) v.push('preview');
    return v;
  }, [devServerPort, agentTabs]);

  // The viewer's own arrangement of the bar: which tabs sit in it and in what
  // order. Availability still comes from `availableViews` above — the layout
  // only reorders and hides. Hidden tabs are not selectable until re-pinned.
  const { orderedViews, customization } = useAgentTabLayout(availableViews);

  // Self-heal: if the URL points at a tab that isn't registered for this agent
  // (plugin not installed, dev server not running, stale shared link), or at a
  // tab the viewer has unpinned, clear/replace the param so the view falls
  // back to a pinned tab (default chat when that is still pinned).
  useEffect(() => {
    if (view !== 'sdk-chat' && !availableViews.includes(view)) {
      void setViewParam(null, { history: 'replace' });
      return;
    }
    if (customization.hidden.includes(view)) {
      const pinned = pinnedAgentViews(orderedViews, customization.hidden);
      const fallback = pinned[0] ?? 'sdk-chat';
      void setViewParam(fallback === 'sdk-chat' ? null : fallback, { history: 'replace' });
    }
  }, [view, availableViews, customization.hidden, orderedViews, setViewParam]);

  // Render fallback: if `view` is not in `availableViews` (e.g. URL points at
  // a tab that hasn't resolved yet, or carried over from a previous agent),
  // or is currently unpinned, render a pinned tab instead of a blank pane
  // while the self-heal effect above updates the URL.
  const effectiveView: AgentView = (() => {
    if (!availableViews.includes(view) || customization.hidden.includes(view)) {
      return pinnedAgentViews(orderedViews, customization.hidden)[0] ?? 'sdk-chat';
    }
    return view;
  })();

  const showInAxonView = useCallback(
    (eventId: string) => {
      // Strip synthetic suffixes (e.g. "-tu-2") added by ClaudeProtocolRenderer
      // so the ID resolves to the real Axon event.
      const baseId = eventId.replace(/-tu-\d+$/, '');
      setHighlightEventId(baseId);
      const axonTab = agentTabs.find((t) => t.id === 'axon');
      if (axonTab && !customization.hidden.includes(axonTab.id)) setView(axonTab.id);
    },
    [agentTabs, customization.hidden, setView],
  );

  const showInChatView = useCallback(
    (eventId?: string) => {
      if (eventId) {
        const baseId = eventId.replace(/-tu-\d+$/, '');
        setHighlightEventId(baseId);
      }
      setView('sdk-chat');
    },
    [setView],
  );

  const handleChatHighlightConsumed = useCallback(() => {
    setHighlightEventId(undefined);
    onHighlightConsumed?.();
  }, [onHighlightConsumed]);

  // Surface this page in the command palette while the agent is open: every
  // pinned tab (so ⌘K can jump straight to e.g. the Assessment tab), the
  // Details panel, and the full agent action set — the same list the header,
  // rail, and detail panel render from, so plugin-contributed tabs and
  // actions come along without the palette ever naming a plugin. Scoped to
  // the live surface (same rule as the number hotkeys) — embedded replays
  // register nothing.
  const actions = useAgentActions(agent, { onArchived, agentActions, activity });
  // Dialog-kind actions opened from the palette render here, independent of
  // the rail's and header's own dialog mounts.
  const [paletteDialogKey, setPaletteDialogKey] = useState<string | null>(null);
  const pageCommands = useMemo<PageCommand[]>(() => {
    if (!numberHotkeys) return [];
    const commands: PageCommand[] = orderedViews
      .filter((v) => !customization.hidden.includes(v))
      .map((v) => ({
        id: `agent-tab-${v}`,
        label: CORE_VIEW_LABEL[v] ?? tabMap.get(v)?.label ?? v,
        icon: viewIconMap[v],
        keywords: ['tab', 'view', 'open'],
        run: () => setView(v),
      }));
    commands.push({
      id: 'agent-open-details',
      label: 'Details',
      icon: Info,
      keywords: ['info', 'panel', 'sandbox', 'open'],
      run: () => setDetailOpen(true),
    });
    if (!hideActions) {
      for (const action of actions) {
        if (action.pending) continue;
        if (action.kind === 'submenu') {
          // Flatten the choices; each item's paletteLabel keeps it readable
          // without the parent trigger ("Mark as blocked", not "Blocked").
          for (const item of action.items) {
            commands.push({
              id: `agent-action-${action.key}-${item.key}`,
              label: item.paletteLabel ?? `${action.label} · ${item.label}`,
              icon: item.icon,
              keywords: [action.label],
              run: item.onSelect,
            });
          }
        } else if (action.kind === 'run') {
          commands.push({
            id: `agent-action-${action.key}`,
            label: action.label,
            icon: action.icon,
            keywords: action.keywords,
            run: action.onSelect,
          });
        } else if (action.kind === 'dialog') {
          commands.push({
            id: `agent-action-${action.key}`,
            label: action.label,
            icon: action.icon,
            keywords: action.keywords,
            run: () => setPaletteDialogKey(action.key),
          });
        } else {
          commands.push({
            id: `agent-action-${action.key}`,
            label: action.label,
            icon: action.icon,
            keywords: action.keywords,
            run: () =>
              /^https?:\/\//i.test(action.to)
                ? window.open(action.to, '_blank', 'noopener,noreferrer')
                : void navigate(action.to),
          });
        }
      }
    }
    return commands;
  }, [
    numberHotkeys,
    orderedViews,
    customization.hidden,
    tabMap,
    viewIconMap,
    setView,
    hideActions,
    actions,
    navigate,
  ]);
  useRegisterPageCommands(pageCommands);

  // Same live-surface gate as the view-tab digits and page commands: embedded
  // replays must not steal ⌥⌘L, and a read-only / actions-hidden session has
  // no Launch again dialog to open. One name for it, because the binding, the
  // menu hint that teaches it, and the dialogs it opens have to agree — a hint
  // shown where nothing is listening teaches a dead chord.
  const sessionHotkeysBound = numberHotkeys && !hideActions;
  useHotkey('launchAgain', () => setPaletteDialogKey('launch-again'), sessionHotkeysBound);

  // Listen for plugin-fired requests to switch the active agent view (e.g.
  // a file-path link in chat asking the Filesystem tab to open). The event
  // is intentionally generic — any tab id contributed by a plugin is honored
  // as long as it's currently registered and still pinned on the rail.
  useEffect(() => {
    function onRequestTab(event: Event) {
      const detail = (event as CustomEvent<{ tabId?: string; agentId?: string }>).detail;
      if (!detail?.tabId) return;
      if (detail.agentId && detail.agentId !== agent.id) return;
      if (availableViews.includes(detail.tabId) && !customization.hidden.includes(detail.tabId)) {
        setView(detail.tabId);
      }
    }
    window.addEventListener('reflex:request-agent-tab', onRequestTab);
    return () => window.removeEventListener('reflex:request-agent-tab', onRequestTab);
  }, [agent.id, availableViews, customization.hidden, setView]);

  const handleSend = useCallback(
    (msg: string, attachments: import('@/lib/types').Attachment[]) => {
      send(msg, attachments);
      // ChatInput's own post-submit cleanup will also clear the draft via the
      // onChange/onAttachmentsChange callbacks, but doing it eagerly here
      // covers the case where the user sends and immediately switches agents
      // (the debounced save effect wouldn't have a chance to run).
      clearDraft();
    },
    [send, clearDraft],
  );

  // A comment left on a spot in an image (a screenshot the agent posted, an
  // attachment on a turn) becomes an ordinary message to this agent, so it
  // lands in the same queue as anything typed in the composer and needs no
  // separate delivery path. Switching to the chat tab keeps the reply visible:
  // the comment is usually left from a screenshot several tabs away.
  const handleImageComment = useCallback(
    (comment: ImageComment) => {
      send(formatImageCommentMessage(comment), []);
      setView('sdk-chat');
    },
    [send, setView],
  );

  // Redirect interrupts the running turn and sends the message now. Only wired
  // when the active provider supports it (the live provider does; replay does
  // not), so the composer hides the redirect affordance during replay.
  const handleRedirect = useMemo(
    () =>
      redirect
        ? (msg: string, attachments: import('@/lib/types').Attachment[]) => {
            redirect(msg, attachments);
            clearDraft();
          }
        : undefined,
    [redirect, clearDraft],
  );

  function renderView() {
    switch (effectiveView) {
      case 'sdk-chat':
        return (
          <>
            <SDKChatView
              events={events ?? []}
              isGenerating={isWorking}
              workingLabel={!isTurnWorking ? (backgroundWorkLabel(agent) ?? undefined) : undefined}
              agentId={agent.id}
              agentModel={agent.model}
              protocol={agent.agentType as 'claude-code' | 'opencode' | 'codex' | null}
              onCommandsChange={setAvailableCommands}
              onActiveModelChange={setActiveModel}
              onActiveQuestionChange={setQuestionPending}
              onLaunchedAgentsChange={setLaunchedSubagents}
              // Same live-surface scoping as the view-tab number hotkeys.
              globalHotkeys={numberHotkeys}
              queuedMessages={queue}
              onEditQueued={updateMessage}
              onDeleteQueued={removeMessage}
              onReorderQueued={reorderQueue}
              onSendQueuedNow={sendQueuedNow}
              pendingMessages={pendingMessages}
              onRetryPending={retryPending}
              onDismissPending={dismissPending}
              highlightEventId={highlightEventId}
              onHighlightConsumed={handleChatHighlightConsumed}
              pagedHistory={pagedHistory}
            />
            {/* Composer and read-only notice ride the transcript's column, so
                the input the user types into is exactly as wide as the messages
                it answers rather than stretching across the whole pane. */}
            <div className={CHAT_COLUMN_CLASS}>
              <AgentActivityDock
                isTurnWorking={isTurnWorking}
                key={agent.id}
                agent={agent}
                events={events ?? []}
                launchedSubagents={launchedSubagents}
                canCancelBackgroundTasks={!isReplay && !agentAccess.isReadOnly}
                // Stream rows jump to their launch event (the same
                // scroll-and-flash path the Axon page's ?eventId= deep link
                // rides); launched-agent rows open the child agent.
                onOpenSubagent={openDockSubagent}
                className="mb-2"
              />
              {agentAccess.isReadOnly && (
                <p
                  className="pb-1 text-xs text-muted-foreground"
                  data-testid="agent-read-only-notice"
                >
                  You have view-only access to this session.
                </p>
              )}
              <ChatInput
                flush
                key={isReplay ? undefined : chatInputKey}
                // The message path expands context markers server-side, so a
                // pasted item URL can become a reference chip here.
                contextReferences
                onSend={handleSend}
                onRedirect={handleRedirect}
                onStop={interrupt}
                // A `view` grant can read the run but not drive it. The server
                // 403s the send either way; disabling stops the composer from
                // inviting a message it will refuse.
                disabled={questionPending || agentAccess.isReadOnly}
                isGenerating={isTurnWorking}
                agentActivity={activity}
                devboxShutDown={devboxShutDown}
                imagesSupported={agentTypeRegistry.modelSupportsImages(
                  agent.agentType,
                  agent.model,
                )}
                chatText={chatText}
                commands={availableCommands}
                initialValue={isReplay ? undefined : draftText}
                initialAttachments={isReplay ? undefined : draftAttachments}
                onChange={isReplay ? undefined : setDraftText}
                onAttachmentsChange={isReplay ? undefined : setDraftAttachments}
                // Same live-surface scoping as the view-tab number hotkeys:
                // provided-context replays never bind global shortcuts.
                globalHotkeys={numberHotkeys}
                contextMeter={contextUsage ? <ContextUsageMeter usage={contextUsage} /> : undefined}
                openMentionRef={openMentionRef}
                leadingToolbar={
                  <>
                    <AgentSettingsChip
                      // The harness is fixed for the run. The model and the
                      // provider key opt back in individually.
                      disabled
                      modelDisabled={!modelSwitch.canSwitch || modelSwitch.pending}
                      modelDisabledReason={modelSwitch.unavailableReason}
                      compact={!isDesktop}
                      agentType={agent.agentType}
                      agentTypeOptions={[
                        {
                          id: agent.agentType,
                          name: runningAgentTypeInfo?.name ?? agent.agentType,
                        },
                      ]}
                      onAgentTypeChange={noop}
                      model={agent.model ?? ''}
                      // Compact label, matching the turn footers. A run that cannot
                      // switch still needs its one option: the trigger's label comes
                      // from the options.
                      modelOptions={
                        modelSwitch.canSwitch
                          ? modelSwitch.options
                          : agent.model
                            ? [{ id: agent.model, name: formatModelLabel(agent.model) }]
                            : []
                      }
                      onModelChange={modelSwitch.onChange}
                      // The provider-key chip in this otherwise read-only composer.
                      // When the run's subscription can be switched (a Claude Max /
                      // Codex agent still able to take a turn), the key icon is
                      // interactive: picking another subscription re-points the run
                      // (applied on its next turn) — the same action as the detail
                      // panel's AgentSubscriptionSelector. Otherwise it stays a
                      // read-only badge of the key resolved at launch, and is
                      // omitted entirely for free-tier runs carrying no secret.
                      providerKey={
                        subscriptionSwitch.canSwitch &&
                        subscriptionSwitch.provider &&
                        agentAccess.canWrite
                          ? {
                              value: agent.modelProviderSecretId ?? null,
                              onChange: subscriptionSwitch.onChange,
                              provider: subscriptionSwitch.provider,
                              allowedTypes: ['subscription'],
                              addSubscription: subscriptionSwitch.affordance,
                              // Re-push the pinned subscription onto the box
                              // without changing it: the repair for a run whose
                              // on-box credential went stale mid-session.
                              refreshSelected: subscriptionSwitch.refresh,
                              // Cog on the user's own subscription rows: re-paste a
                              // credential that expired without leaving the run.
                              manageSubscription: subscriptionSwitch.manage,
                              dialog: subscriptionSwitch.dialog,
                              owner: agent.userId ? { ownerId: agent.userId } : undefined,
                              // Interactive even though the chip is `disabled`.
                              disabled: subscriptionSwitch.pending,
                              compact: true,
                              highlight: agent.credentialSyncPending === true,
                              // `onChange` is a switch mutation, so the picker must
                              // never emit a default of its own — opening a run
                              // whose key isn't in the subscription list would
                              // otherwise fire an unrequested switch.
                              autoSelectDefault: false,
                            }
                          : agent.modelProviderSecretId
                            ? {
                                value: agent.modelProviderSecretId,
                                onChange: noop,
                                provider: toModelProvider(null, agent.model ?? ''),
                                allowedTypes: ['apiKey', 'subscription'],
                                disabled: true,
                                compact: true,
                              }
                            : undefined
                      }
                    />
                    {/* Context sits after the agent/model/provider chip: the chip
                      is what the run *is*, the stack is what has been added to
                      its box. The "+" opens the same `@` menu the composer
                      does, where the MCP Servers category attaches a server to
                      the live run. */}
                    {contextStackEnabled && (
                      <AgentContextStack
                        onAdd={canAttachMcp ? () => openMentionRef.current?.() : undefined}
                        attachmentValues={appliedAttachments}
                      />
                    )}
                  </>
                }
                renderMentionMenu={({
                  query,
                  style,
                  onClose,
                  clearMentionToken,
                  insertMention,
                  resetMentionQuery,
                  setMentionQuery,
                  reportResults,
                  menuRef,
                }) => (
                  <LaunchMentionMenu
                    ref={menuRef}
                    contextOnly
                    includeRuntimeConfig={canAttachMcp}
                    agentId={agent.id}
                    query={query}
                    style={style}
                    attachments={appliedAttachments}
                    repoSlug={getGitRepoAttachment(agent)?.repoSlug}
                    selectedPersonaId={null}
                    onResetQuery={resetMentionQuery}
                    onScopeCategory={(keyword) => setMentionQuery(`${keyword}:`)}
                    onResultsChange={reportResults}
                    onApply={(result) => {
                      // Agent chat references context — other runs and plugin
                      // context items (PRs, tickets, issues, …) — runs a
                      // plugin action on this agent, or attaches a
                      // runtime-attachable capability (MCP servers) to the
                      // live run. The other launch-config result kinds
                      // (personas, repos, sandbox) stay deliberately
                      // unhandled: they cannot change mid-run.
                      if (result.kind === 'reference-agent-run') {
                        insertMention({
                          kind: 'agent-run',
                          id: result.agentId,
                          title: result.title,
                        });
                      } else if (result.kind === 'reference-context') {
                        insertMention({
                          kind: 'context',
                          contextKind: result.contextKind,
                          id: result.id,
                          title: result.title,
                        });
                      } else {
                        // Everything else configures the box rather than writing
                        // a message, so the `@…` the pick was made through goes
                        // — the same rule, and the same default-clean shape, as
                        // the launch dialog. Left behind, the token rides along
                        // into the next message the user sends.
                        clearMentionToken();
                        if (
                          result.kind === 'set-attachment' &&
                          result.attachmentId === MCP_SERVERS_ATTACHMENT_ID
                        ) {
                          mcpAttach.onAttach(result.config);
                        } else if (result.kind === 'custom') {
                          result.run();
                        }
                      }
                      onClose();
                    }}
                    onClose={onClose}
                  />
                )}
              />
            </div>
          </>
        );
      case 'preview':
        return devServerPort ? (
          <AgentPreviewPanel
            agentId={agent.id}
            agentStatus={agent.status}
            devboxId={agent.devboxId}
            tunnelKey={agent.tunnelKey}
            port={devServerPort}
          />
        ) : null;
      default: {
        const tab = tabMap.get(effectiveView);
        if (!tab) return null;
        const TabComponent = tab.component as ComponentType<{
          agent: Agent;
          events: unknown[];
          highlightEventId?: string;
          onHighlightEvent?: (eventId: string) => void;
        }>;
        return (
          <Suspense fallback={<AgentTabSuspenseFallback />}>
            <TabComponent
              agent={agent}
              // Tabs derive from the whole session (changed files, copy-all,
              // assessments), so they get the full history, not the window.
              events={fullEvents ?? []}
              highlightEventId={highlightEventId}
              onHighlightEvent={showInAxonView}
            />
          </Suspense>
        );
      }
    }
  }

  return (
    // Expose the agent id to content-scanner components (filesystem file-path
    // popover, ...) rendered in any of this agent's markdown surfaces.
    <MarkdownAgentIdProvider agentId={agent.id}>
      <div className="flex h-full flex-col">
        <AgentHeaderBar
          agent={agent}
          activity={activity}
          hideActions={hideActions}
          hotkeysBound={sessionHotkeysBound}
          agentActions={agentActions}
          agentHeaderBadges={agentHeaderBadges}
          onToggleSidebar={onToggleSidebar}
          sidebarToggleAlwaysVisible={sidebarToggleAlwaysVisible}
          onArchived={onArchived}
          mobileSubHeader={
            !isDesktop ? (
              <MobileViewStrip
                activeView={effectiveView}
                items={orderedViews
                  .filter((v) => !customization.hidden.includes(v))
                  .map((v) => ({
                    value: v,
                    label: CORE_VIEW_LABEL[v] ?? tabMap.get(v)?.label ?? v,
                    icon: viewIconMap[v],
                  }))}
                onViewChange={setView}
                trailing={
                  <MobileToolsSheet
                    tools={(dismiss) => (
                      <AgentRailToolbar
                        agent={agent}
                        activity={activity}
                        hideActions={hideActions}
                        agentActions={agentActions}
                        onOpenDetail={() => setDetailOpen(true)}
                        onArchived={onArchived}
                        onDismissSheet={dismiss}
                        availableViews={orderedViews}
                        activeView={effectiveView}
                        onViewChange={setView}
                        viewIconMap={viewIconMap}
                        viewLabelMap={CORE_VIEW_LABEL}
                        tabMap={tabMap}
                        customize={customization}
                        layout="toolbar"
                        menuSide="top"
                      />
                    )}
                  />
                }
              />
            ) : undefined
          }
        />

        <ShareRequestsBanner agent={agent} />

        <AxonViewContext.Provider value={{ showInAxonView, showInChatView }}>
          <ImageCommentProvider onComment={handleImageComment}>
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* Owner of the chat column width for this pane: the transcript
                  is a plugin's, the composer below it is ours, and the resizer
                  spans both, so the measurement they share is published here,
                  at the one element that contains all three. */}
              <div
                className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                style={chatColumnStyle(chatColumnWidth)}
              >
                {renderView()}
                {/* Only the chat reads the column width; the other views fill
                    the pane and have no edges to drag. */}
                {effectiveView === 'sdk-chat' && <ChatColumnResizer />}
              </div>
              <VerticalTabRail
                value={effectiveView}
                onValueChange={setView}
                aria-label="Agent view"
                numberHotkeys={numberHotkeys}
                hidden={customization.hidden}
                customize={{ onOrderChange: customization.onOrderChange }}
                className="hidden md:flex"
                items={orderedViews.map((v) => ({
                  value: v,
                  label: CORE_VIEW_LABEL[v] ?? tabMap.get(v)?.label ?? v,
                  icon: viewIconMap[v],
                  dataTutorialId: `agent-tab-${v}`,
                }))}
                bottomContent={
                  <AgentRailToolbar
                    agent={agent}
                    activity={activity}
                    hideActions={hideActions}
                    agentActions={agentActions}
                    onOpenDetail={() => setDetailOpen(true)}
                    onArchived={onArchived}
                    availableViews={orderedViews}
                    activeView={effectiveView}
                    onViewChange={setView}
                    viewIconMap={viewIconMap}
                    viewLabelMap={CORE_VIEW_LABEL}
                    tabMap={tabMap}
                    customize={customization}
                  />
                }
              />
            </div>
          </ImageCommentProvider>
        </AxonViewContext.Provider>

        <AgentDetailPanel agent={agent} open={detailOpen} onClose={() => setDetailOpen(false)} />
        {sessionHotkeysBound ? (
          <AgentActionDialogs
            actions={actions}
            agent={agent}
            openKey={paletteDialogKey}
            onOpenKeyChange={setPaletteDialogKey}
          />
        ) : null}
      </div>
    </MarkdownAgentIdProvider>
  );
}

function AgentTabSuspenseFallback() {
  return (
    <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="icon-sm animate-spin" />
      <span className="text-xs">Loading…</span>
    </div>
  );
}

/**
 * Public wrapper: if an `AgentStreamDataContext` is already provided by a
 * parent (e.g. the replay page), use it directly. Otherwise wrap the inner
 * component with the default live-data provider.
 */
export function AgentStreamView(props: AgentStreamViewProps) {
  const existingCtx = useContext(AgentStreamDataContext);
  if (existingCtx) {
    // Provided-context surfaces (replay pages, marketing/docs replay blocks)
    // can mount several stream views on one page and are often decorative —
    // never bind the global view-switching hotkey there.
    return <AgentStreamViewInner {...props} />;
  }
  return (
    <DefaultAgentStreamDataProvider agent={props.agent}>
      <AgentStreamViewInner {...props} numberHotkeys />
    </DefaultAgentStreamDataProvider>
  );
}
